/**
 * VMs Page Logic
 *
 * Virtual machines list and management
 */

import { api } from '../api/client.js';
import { apiCall } from '../api/helpers.js';
import { connectVmStatus, connectRemoteHostStatus, disconnectVmStatus } from '../api/vmstatus-ws.js';
import { initAuthenticatedPage } from '../app/init.js';
import { initVmTable, updateVmInTable, stopUptimeTimer, setVisibleColumns } from '../vm/table.js';
import { vms } from '../globals.js';
import { vmModifyOrCreate } from '../vm/modify.js';
import { declareLoading, declareLoaded, confirmDialog } from '../ui/dialogs.js';

/**
 * Start all VMs
 */
export async function startAllVms(): Promise<void> {
  confirmDialog('Start all VMs?', async () => {
    declareLoading();
    await apiCall(api.startAllVms(), 'Failed to Start All VMs');
    declareLoaded();
  });
}

/**
 * Stop all VMs
 */
export async function stopAllVms(): Promise<void> {
  confirmDialog('Stop all VMs? This will gracefully shut down all running VMs.', async () => {
    declareLoading();
    await apiCall(api.stopAllVms(), 'Failed to Stop All VMs');
    declareLoaded();
  });
}

/**
 * Initialize the VMs page
 */
export async function initVmsPage(): Promise<void> {
  declareLoading();

  const user = await initAuthenticatedPage();
  if (!user) return; // Will redirect to login

  // Apply user's column visibility preferences before rendering
  // (Name, Description, and Actions are always shown — not toggleable)
  const defaultCols = ['ip_address', 'rdp_users'];
  const saved = user.settings?.vm_columns as string[] | undefined;
  const vmColumns = saved?.length ? saved : defaultCols;
  setVisibleColumns(vmColumns);

  // Fetch schema + VM list, render table
  await initVmTable();

  // Show page once WS sends initial status (fallback: 5s timeout)
  const loadedTimeout = setTimeout(declareLoaded, 5000);

  // Connect WebSocket for real-time status updates
  connectVmStatus(
    (status) => { updateVmInTable(status); },
    () => { clearTimeout(loadedTimeout); declareLoaded(); },
  );

  // Connect to remote hosts for delegated VM status
  const remoteHosts = [...new Set(vms.map(vm => vm.host).filter(Boolean))] as string[];
  if (remoteHosts.length > 0) {
    connectRemoteHostStatus(remoteHosts);
  }
}

/**
 * Clean up when leaving the page
 */
export function cleanupVmsPage(): void {
  stopUptimeTimer();
  disconnectVmStatus();
}

// Export to window for onclick handlers
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).startAllVms = startAllVms;
  (window as unknown as Record<string, unknown>).stopAllVms = stopAllVms;
  (window as unknown as Record<string, unknown>).vmModifyOrCreate = vmModifyOrCreate;
  (window as unknown as Record<string, unknown>).initVmsPage = initVmsPage;
  (window as unknown as Record<string, unknown>).cleanupVmsPage = cleanupVmsPage;

  // Clean up on page unload
  window.addEventListener('beforeunload', cleanupVmsPage);
}
